/**
 * What Sky learns, and how.
 *
 * Nothing here is a model being trained. It is a record: every question, which
 * of her layers answered it (or that none did), and the screen the person went
 * to next when she had no answer — because that is the answer they were
 * after. The next time the same question comes, she opens that screen and
 * says why. What she could not answer is kept as a list the airport can read
 * in Settings and act on: a phrase to teach her, a query to add to the
 * platform's catalogue.
 *
 * Kept per person in this browser, and mirrored to the platform's preference
 * store under the sign-in when it will take it, so what she learned on one
 * workstation is there on the next.
 */
import type { DataSource } from "../api/dataSource";

export type Outcome =
  | "screen"
  | "platform"
  | "section"
  | "briefing"
  | "guide"
  | "reading"
  | "search"
  | "model"
  | "taught"
  | "refused"
  | "none";

export interface Asked {
  q: string;
  at: number;
  outcome: Outcome;
  route: string;
}

export interface Learning {
  asked: Asked[];
  /** a normalised question → the screen that answered it */
  taught: Record<string, { route: string; title: string; at: number; times: number }>;
  version: 1;
}

const KEY = (who: string) => `skylogics.assistant.learning.${who}`;
const PREFERENCE_KEY = "assistant.learning";
const MAX_ASKED = 300;
/** a question is "unanswered" for this long: a screen opened within it is taken as the answer */
export const TEACH_WINDOW_MS = 90_000;

const empty = (): Learning => ({ asked: [], taught: {}, version: 1 });

export function load(who: string): Learning {
  try {
    const raw = localStorage.getItem(KEY(who));
    if (!raw) return empty();
    const v = JSON.parse(raw) as Partial<Learning>;
    return { asked: v.asked ?? [], taught: v.taught ?? {}, version: 1 };
  } catch {
    return empty();
  }
}

function save(who: string, l: Learning, source?: DataSource): void {
  try {
    localStorage.setItem(KEY(who), JSON.stringify(l));
  } catch {
    /* storage off */
  }
  /* mirrored to the sign-in when the platform will take it; silence otherwise */
  source?.setPreference(PREFERENCE_KEY, l).catch(() => undefined);
}

/** Bring what the platform holds under this sign-in into this browser, once, when it is newer. */
export async function pull(who: string, source: DataSource): Promise<void> {
  try {
    const remote = (await source.getPreference(PREFERENCE_KEY)) as Partial<Learning> | null;
    if (!remote?.asked) return;
    const local = load(who);
    const newest = (l: Partial<Learning>) => Math.max(0, ...(l.asked ?? []).map((a) => a.at));
    if (newest(remote) > newest(local)) {
      try {
        localStorage.setItem(KEY(who), JSON.stringify({ asked: remote.asked, taught: remote.taught ?? {}, version: 1 }));
      } catch {
        /* storage off */
      }
    }
  } catch {
    /* no platform, or no preference */
  }
}

/** Words that carry meaning, lower-cased, so two phrasings of one question meet. */
export function normalise(q: string): string {
  return q
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOP.has(w))
    .sort()
    .join(" ");
}
const STOP = new Set(["the", "and", "for", "what", "how", "are", "is", "was", "were", "can", "you", "tell", "about", "with", "this", "that", "there", "please", "sky", "show", "give"]);

export function recordAsk(who: string, q: string, outcome: Outcome, route: string, source?: DataSource): Learning {
  const l = load(who);
  l.asked.push({ q, at: Date.now(), outcome, route });
  if (l.asked.length > MAX_ASKED) l.asked.splice(0, l.asked.length - MAX_ASKED);
  save(who, l, source);
  return l;
}

/** The last question she had no answer for, if it was inside the teaching window. */
export function pendingUnanswered(who: string, now = Date.now()): Asked | null {
  const l = load(who);
  const last = l.asked[l.asked.length - 1];
  if (!last || last.outcome !== "none" && last.outcome !== "search") return null;
  return now - last.at <= TEACH_WINDOW_MS ? last : null;
}

/** The person went to a screen right after a question she could not answer: that screen is the answer. */
export function learnRoute(who: string, q: string, route: string, title: string, source?: DataSource): void {
  const l = load(who);
  const k = normalise(q);
  if (!k) return;
  const prev = l.taught[k];
  l.taught[k] = { route, title, at: Date.now(), times: (prev?.times ?? 0) + 1 };
  save(who, l, source);
}

/** A screen she was taught for this question, or one close enough to it. */
export function taughtRoute(who: string, q: string): { route: string; title: string; times: number } | null {
  const l = load(who);
  const k = normalise(q);
  if (!k) return null;
  if (l.taught[k]) return l.taught[k];
  const words = new Set(k.split(" "));
  let best: { route: string; title: string; times: number; score: number } | null = null;
  for (const [key, t] of Object.entries(l.taught)) {
    const other = key.split(" ");
    const overlap = other.filter((w) => words.has(w)).length;
    const score = overlap / Math.max(words.size, other.length);
    if (score >= 0.6 && (!best || score > best.score)) best = { ...t, score };
  }
  return best;
}

export interface LearningSummary {
  asked: number;
  answered: number;
  unanswered: { q: string; times: number; last: number }[];
  taught: { q: string; route: string; title: string; times: number }[];
  byOutcome: Partial<Record<Outcome, number>>;
}

export function summary(who: string): LearningSummary {
  const l = load(who);
  const byOutcome: Partial<Record<Outcome, number>> = {};
  const un = new Map<string, { q: string; times: number; last: number }>();
  for (const a of l.asked) {
    byOutcome[a.outcome] = (byOutcome[a.outcome] ?? 0) + 1;
    if (a.outcome === "none" || a.outcome === "search") {
      const k = normalise(a.q);
      const cur = un.get(k) ?? { q: a.q, times: 0, last: 0 };
      cur.times += 1;
      cur.last = Math.max(cur.last, a.at);
      un.set(k, cur);
    }
  }
  return {
    asked: l.asked.length,
    answered: l.asked.filter((a) => a.outcome !== "none" && a.outcome !== "refused").length,
    unanswered: [...un.values()].sort((a, b) => b.times - a.times || b.last - a.last).slice(0, 20),
    taught: Object.entries(l.taught).map(([q, t]) => ({ q, ...t })).sort((a, b) => b.times - a.times),
    byOutcome,
  };
}

/** What she says when asked what she has learned. */
export function learningLines(who: string): string[] {
  const s = summary(who);
  if (!s.asked) return ["Nothing yet: this is the first thing you have asked me on this sign-in."];
  const lines = [`You have asked me ${s.asked} thing${s.asked === 1 ? "" : "s"}; I answered ${s.answered} of them from the screen, the platform or my guide.`];
  if (s.taught.length) lines.push(`You have taught me ${s.taught.length} route${s.taught.length === 1 ? "" : "s"}: ${s.taught.slice(0, 3).map((t) => `"${t.q}" → ${t.title}`).join("; ")}.`);
  if (s.unanswered.length) lines.push(`I still cannot answer ${s.unanswered.length}: ${s.unanswered.slice(0, 3).map((u) => `"${u.q}"`).join(", ")}. Open the screen that answers one and I will remember it.`);
  else lines.push("Nothing is outstanding.");
  return lines;
}

export function forget(who: string, source?: DataSource): void {
  save(who, empty(), source);
}

export function isLearningQuestion(q: string): boolean {
  return /\b(what have you learn|what did you learn|what can't you answer|what cannot you answer|what do you (not )?know|how (much|many) have i asked|your (memory|learning))/i.test(q);
}
